import { removeElements } from '@zurich-framework/core/utils';
import { DefaultValue as RecoilDefaultValue, selectorFamily, } from 'recoil';
import { devError } from '../../utils';
import { alertOverwrite, filterArray, findIndexByFilter, mergeArrayIndices, } from '../values';
/**
 * Create a *selectorFamily* that:
 *  - **Gets** baseSelector[index]
 *  - **Sets** baseSelector[index]
 * @example
 *  ['a', 'b'] => selectorFamily(0) => 'a'
 * @param baseSelector array of elements selector or atom
 * @param options.defaultValue required defaultValue for created selector
 * @returns selectorFamily
 */
export const createArrayByIndexSelectorFamily = (baseSelector, options
// TODO: defaultValue does not type properly here
) => {
    if (!baseSelector) {
        // TODO: cleanup
        throw new Error('Please define a valid baseSelector param');
    }
    return selectorFamily({
        key: `${baseSelector.key}/arrayByIndex`,
        get: ({ index }) => ({ get }) => {
            const arr = get(baseSelector);
            return Array.isArray(arr)
                ? (arr[index] ?? options.defaultValue)
                : options.defaultValue;
        },
        set: ({ index }) => ({ get, set }, value) => {
            const items = get(baseSelector);
            if (value instanceof RecoilDefaultValue) {
                // reset by removing element from array
                if (Array.isArray(items))
                    set(baseSelector, removeElements(items, index, 1));
                return;
            }
            // merge entry with changes into array
            set(baseSelector, mergeArrayIndices(Array.isArray(items) ? items : [], index, value));
        },
    });
};
/**
 * Create a *selectorFamily* that:
 *  - **Gets** object from an array by a filter (Partial<object>)
 *  - **Sets** setting into that object by the filter
 * @example
 *  [{name: Muller, age: 30, insurance: zurich1}, {name: Muller, age: 31, insurance: zurich2}] => selectorFamily({name: Muller, age: 31 }) => {name: Muller, age: 31, insurance: zurich2}
 * @param baseSelector selector or atom for array of objects which contain a field (defined in fieldName) which has a unique value (e.g. an id)
 * @param fieldNames name of unique key in object
 * @param options.defaultValue required defaultValue for created selector
 * @returns selectorFamily
 */
export const createArrayByFilterSelectorFamily = (baseSelector, uniqueKey, options) => {
    if (!baseSelector) {
        throw new Error('Please define a valid baseSelector param');
    }
    return selectorFamily({
        key: `${baseSelector.key}/${uniqueKey}`,
        get: (filter) => ({ get }) => {
            const arr = get(baseSelector);
            if (Array.isArray(arr)) {
                const tmpArray = filterArray(arr, filter);
                if (tmpArray.length === 1) {
                    return tmpArray[0];
                }
                if (tmpArray.length > 1) {
                    devError('Warning - NMP - common/selectorFamily/get - more than on element found for filter', JSON.stringify(filter));
                    return tmpArray[0];
                }
            }
            return options.defaultValue;
        },
        set: (filter) => ({ get, set }, value) => {
            const items = get(baseSelector);
            let index = -2;
            // if necessary informs the user that they do something wrong
            alertOverwrite(value, filter);
            const itemsIsArray = Array.isArray(items);
            if (itemsIsArray) {
                // we need to check first if there is more than 1 element with the same fields
                // in other words our filter should produce one result and not more than that
                const tmpArray = filterArray(items, filter);
                if (tmpArray.length > 1) {
                    console.error('Warning - NMP - common/selectorFamily/set - Error more than one element found in array that fits with the provided filter', filter);
                }
                else if (tmpArray.length === 1) {
                    index = findIndexByFilter(items, filter);
                }
                else {
                    index = -1;
                }
            }
            if (value instanceof RecoilDefaultValue) {
                // reset by removing element from array
                if (itemsIsArray && index)
                    set(baseSelector, removeElements(items, index, 1));
                return;
            }
            if (index === -2) {
                // do nothing
                return;
            }
            // merge entry with changes into array
            if (index !== -1) {
                set(baseSelector, mergeArrayIndices(itemsIsArray ? items : [], index, {
                    ...value,
                    ...filter,
                }));
                return;
            }
            // if the element does not exist yet, we append it
            set(baseSelector, [
                ...items,
                { ...options.defaultValue, ...value, ...filter },
            ]);
        },
    });
};
/**
 * Create a *selectorFamily* that:
 *  - **Gets** object from an array by object's unique key (using `array.find(...)`)
 *  - **Sets** setting into that object by the unique key
 * @example
 *  [{id: 1}, {id: 2}] => selectorFamily({ id: 1 }) => {id: 1}
 * @param baseSelector selector or atom for array of objects which contain a field (defined in fieldName) which has a unique value (e.g. an id)
 * @param fieldName name of unique key in object
 * @param options.defaultValue required defaultValue for created selector
 * @returns selectorFamily
 */
export function createArrayByUniqueKeySelectorFamily(baseSelector, fieldName, options) {
    if (!baseSelector) {
        throw new Error('Please define a valid baseSelector param');
    }
    return selectorFamily({
        key: `${baseSelector.key}/${String(fieldName)}`,
        get: ({ [fieldName]: keyValue }) => ({ get }) => {
            const arr = get(baseSelector);
            return Array.isArray(arr)
                ? (arr.find((e) => e && e[fieldName] ? e[fieldName] === keyValue : false) ?? options.defaultValue)
                : options.defaultValue;
        },
        set: ({ [fieldName]: keyValue }) => ({ get, set }, value) => {
            const items = get(baseSelector);
            // get index of element in array, -1 means not found (also returned like that from findIndex)
            const index = Array.isArray(items)
                ? items?.findIndex((i) => i && i[fieldName] ? i[fieldName] === keyValue : false)
                : -1;
            if (value instanceof RecoilDefaultValue) {
                // reset by removing element from array
                if (Array.isArray(items) && index)
                    set(baseSelector, removeElements(items, index, 1));
                return;
            }
            // merge entry with changes into array
            if (index !== -1) {
                set(baseSelector, mergeArrayIndices(Array.isArray(items) ? items.filter((i) => !!i) : [], index, {
                    ...value,
                    // FIXME: Overwrite of the incoming values, I get the point but, wouldn't it be right to inform the user that they did something wrong?
                    [fieldName]: keyValue,
                }));
                return;
            }
            // if the element does not exist yet, we append it
            set(baseSelector, [
                ...items,
                { ...options.defaultValue, ...value, [fieldName]: keyValue },
            ]);
        },
    });
}
/**
 * Create a *selectorFamily* that extends an existing selectorFamily and:
 *  - **Gets** object from an array by object's unique key (using `array.find(...)`)
 *  - **Sets** setting into that object by the unique key
 * @example
 *  [{id: 1}, {id: 2}] => selectorFamily({ ...previousParams, id: 1 }) => {id: 1}
 * @param baseSelector selectorFamily for array of objects which contain a field (defined in fieldName) which has a unique value (e.g. an id)
 * @param fieldName name of unique key in object
 * @param options.defaultValue required defaultValue for created selector
 * @returns selectorFamily
 */
export function createArrayByUniqueKeyHigherOrderSelectorFamily(baseSelector, fieldName, options) {
    if (!baseSelector) {
        throw new Error('Please define a valid baseSelector param');
    }
    return selectorFamily({
        key: `${baseSelector.name}/${String(fieldName)}`,
        get: ({ [fieldName]: keyValue, ...params }) => ({ get }) => {
            // this cannot be typed because we don't know which properties can be in params
            const arr = get(baseSelector(params));
            return Array.isArray(arr)
                ? (arr.find((e) => e && e[fieldName]
                    ? e[fieldName] === keyValue
                    : false) ?? options.defaultValue)
                : options.defaultValue;
        },
        set: ({ [fieldName]: keyValue, ...params }) => ({ get, set }, value) => {
            // this cannot be typed because we don't know which properties can be in params
            const selector = baseSelector(params);
            const items = get(selector);
            // get index of element in array, -1 means not found (also returned like that from findIndex)
            const index = Array.isArray(items)
                ? items?.findIndex((i) => i && i[fieldName]
                    ? i[fieldName] === keyValue
                    : false)
                : -1;
            if (value instanceof RecoilDefaultValue) {
                // reset by removing element from array
                if (Array.isArray(items) && index)
                    set(selector, removeElements(items, index, 1));
                return;
            }
            // merge entry with changes into array
            if (index !== -1) {
                set(selector, mergeArrayIndices(Array.isArray(items)
                    ? items.filter((i) => !!i)
                    : [], index, {
                    [fieldName]: keyValue,
                    ...value,
                }));
            }
            else {
                // if the element does not exist yet, we append it
                set(selector, [
                    ...items,
                    { [fieldName]: keyValue, ...options.defaultValue, ...value },
                ]);
            }
        },
    });
}
/**
 * Create a *selectorFamily* that:
 *  - **Gets** objects from an array by object's key (using `array.filter(...)`)
 *  - **Sets** setting into objects that match the keys value
 * @example
 *  [{type: 'a'}, {type: 'a'}, {type: 'b'}] => selectorFamily({ type: 'a' }) => [{type: 'a'}, {type: 'a'}]
 * @param baseSelector selector or atom for array of objects which contain a field (defined in fieldName)
 * @param fieldName name of key in object
 * @returns selectorFamily
 */
export const createArrayByKeySelectorFamily = (baseSelector, fieldName) => {
    if (!baseSelector) {
        throw new Error('Please define a valid baseSelector param');
    }
    return selectorFamily({
        key: `${baseSelector.key}/arrayByKey`,
        get: ({ [fieldName]: keyValue }) => ({ get }) => {
            const arr = get(baseSelector);
            return Array.isArray(arr)
                ? arr.filter((e) => e[fieldName] === keyValue)
                : [];
        },
        set: ({ [fieldName]: keyValue }) => ({ set }, value) => {
            const selector = baseSelector;
            set(selector, (prev) => [
                ...(prev || []).filter((e) => e[fieldName] !== keyValue),
                ...(value instanceof RecoilDefaultValue
                    ? []
                    : value.map((v) => ({ [fieldName]: keyValue, ...v }))),
            ]);
        },
    });
};
/**
 * Create field helper based on existing selectorFamily
 * @param baseSelector selectorFamily object
 * @returns function
 */
export const createFieldSelectorFamily = (baseSelector) => 
/**
 * Create a *selectorFamily* that:
 *  - **Gets** baseSelector[fieldName]
 *  - **Sets** baseSelector[fieldName]
 * @example
 *  {field: 'value', type: 'a'} => selectorFamily({...previousParams }) => 'value'
 * @param fieldName name of key in object
 * @param options.defaultValue required defaultValue for created selector
 * @returns selectorFamily
 */
(fieldName, options) => {
    if (!baseSelector) {
        throw new Error('Please define a valid baseSelector param');
    }
    const key = `fieldSelectorFamily/${String(fieldName)}`;
    return selectorFamily({
        key,
        get: (params) => ({ get }) => {
            const base = get(baseSelector(params));
            // TODO: this could probably type better
            const field = ((base && base[fieldName]) ??
                options.defaultValue);
            return field;
        },
        set: (params) => ({ set }, value) => {
            const selector = baseSelector(params);
            set(selector, (prev) => ({
                ...prev,
                [fieldName]: value instanceof RecoilDefaultValue
                    ? options.defaultValue
                    : value,
            }));
        },
    });
};
/**
 * Create a *selectorFamily* that extends an existing selectorFamily and:
 *  - **Gets** field from any object of an array
 *  - **Sets** that field in all objects of the array (without changing other fields in object)
 * @example
 *  [{fieldName: 'value', type: 'b'}, {fieldName: 'value', type: 'a'}] => selector() => 'value'
 * @param anySelector selectorFamily used as getter to get any element (e.g. selector from createArrayAnySelector(x))
 * @param allSelector selectorFamily used as setter to write into all elements (e.g. selector "x" used in any selector)
 * @param fieldName name of key in object
 * @param options.defaultValue required defaultValue for created selector
 * @returns selectorFamily
 */
export const createArrayAnyFieldHigherOrderSelectorFamily = (anySelectorFamily, allSelectorFamily, fieldName, options) => {
    if (!anySelectorFamily) {
        throw new Error('Please define a valid anySelectorFamily param');
    }
    const key = `${anySelectorFamily.name}/${String(fieldName)}/anyFieldHigherOrder`;
    return selectorFamily({
        key,
        get: ({ [fieldName]: _keyVal, ...params }) => ({ get }) => {
            // this cannot be typed because we don't know which properties can be in params
            const any = get(anySelectorFamily(params));
            // TODO: this could probably type better
            const field = ((any && any[fieldName]) ??
                options.defaultValue);
            return field;
        },
        set: ({ [fieldName]: _keyVal, ...params }) => ({ set }, value) => {
            // merge in all array elements
            const update = value instanceof RecoilDefaultValue
                ? { [fieldName]: options.defaultValue }
                : { [fieldName]: value };
            // this cannot be typed because we don't know which properties can be in params
            set(allSelectorFamily(params), (prev) => prev.map((e) => ({
                ...e,
                ...update,
            })));
        },
    });
};
/**
 * Create a *selectorFamily* that allows you to use an array as if it was a single element:
 *  - **Gets** any element from the array
 *  - **Sets** the element provided to *all* elements of the array
 * @example
 *  [{a: 'c'}, {a: 'b'}, {a: 'c'}] => selectorFamily => {a: 'c'}
 *  selectorFamily({a : 'a'}) => [{a: 'a'}, {a: 'a'}, {a: 'a'}]
 * @param baseSelector selectorFamily, returning an array
 * @param options.defaultValue required defaultValue for created selector
 * @returns selector
 */
export const createArrayAnySelectorFamily = (baseSelector, options) => selectorFamily({
    key: `${baseSelector.name}/arrayAny`,
    get: (params) => ({ get }) => {
        const arr = get(baseSelector(params));
        return Array.isArray(arr)
            ? (arr[0] ?? options.defaultValue)
            : options.defaultValue;
    },
    set: (params) => ({ set }, value) => {
        // set in all array elements
        set(baseSelector(params), (prevItems) => value instanceof RecoilDefaultValue
            ? []
            : prevItems?.length === 0
                ? [value]
                : prevItems.map(() => value));
        return;
    },
});
/**
 * Create a *selectorFamily* that can handle any usecases where we want to iterate into that element:
 *  - **Gets** field from any object of an array
 *  - **Sets** that field in all objects of the array (without changing other fields in object)
 * @example
 *  [{fieldName: 'value', type: 'b'}, {fieldName: 'value', type: 'a'}] => selector() => 'value'
 * @param anySelector selectorFamily used as getter to get any element (e.g. selector from createArrayAnySelector(x))
 * @param allSelector selectorFamily used as setter to write into all elements (e.g. selector "x" used in any selector)
 * @param arrayFieldName name of key in object pointing to array we want to search in
 * @param keyFieldName name of key we want to use to select element of array
 * @param options.defaultValue required defaultValue for created selector
 * @returns selectorFamily
 */
export const createArrayAnyFieldByUniqueKeyHigherOrderSelectorFamily = (anySelectorFamily, allSelectorFamily, arrayFieldName, keyFieldName, options) => {
    if (!anySelectorFamily) {
        throw new Error('Please define a valid anySelectorFamily param');
    }
    const key = `${anySelectorFamily.name}/${String(arrayFieldName)}/${String(keyFieldName)}/anyFieldHigherOrder`;
    return selectorFamily({
        key,
        get: ({ [arrayFieldName]: _arrayKeyVal, [keyFieldName]: keyKeyVal, ...params }) => ({ get }) => {
            // this cannot be typed because we don't know which properties can be in params
            const any = get(anySelectorFamily(params));
            // TODO: this could probably type better
            const field = (any && any[arrayFieldName]) ?? [];
            if (!Array.isArray(field)) {
                // this should never execute
                return options.defaultValue;
            }
            return (field?.find((f) => f[keyFieldName] === keyKeyVal) ??
                options.defaultValue);
        },
        set: ({ [arrayFieldName]: _keyVal, [keyFieldName]: keyKeyVal, ...params }) => ({ set }, value) => {
            // merge in all array elements
            // this cannot be typed because we don't know which properties can be in params
            set(allSelectorFamily(params), (prev) => prev.map((e) => {
                const items = e[arrayFieldName];
                let res = [];
                // get index of element in array, -1 means not found (also returned like that from findIndex)
                const index = Array.isArray(items)
                    ? items?.findIndex((i) => i[keyFieldName] ===
                        keyKeyVal)
                    : -1;
                if (value instanceof RecoilDefaultValue) {
                    // reset by removing element from array
                    if (Array.isArray(items) && index) {
                        res = removeElements(items, index, 1);
                    }
                }
                // merge entry with changes into array
                if (index !== undefined && index !== -1) {
                    res = mergeArrayIndices(Array.isArray(items) ? items : [], index, {
                        [keyFieldName]: keyKeyVal,
                        ...value,
                    });
                }
                else {
                    // if the element does not exist yet, we append it
                    res = [
                        ...(Array.isArray(items) ? items : []),
                        {
                            [keyFieldName]: keyKeyVal,
                            ...options.defaultValue,
                            ...value,
                        },
                    ];
                }
                return {
                    ...e,
                    [arrayFieldName]: res,
                };
            }));
        },
    });
};
/**
 * Helper to populat `{key: [{ key: [ ... ]}]}` structure recursively
 * This updates values as well as create those that do not exists by setting a defaultValue
 * This is still rather UNSTABLE as it does not fully type
 * @param instructions
 * @param obj
 * @returns
 */
export function updateDeepArrayObjects(instructions, obj) {
    const { fieldName, keyName, id, defaultValue, value, child } = instructions;
    const exists = obj[fieldName]?.find((el) => el[keyName] === id);
    return {
        ...obj,
        [fieldName]: exists
            ? obj[fieldName].map((el) => el[keyName] === id // overwrite match
                ? {
                    ...el,
                    ...(child // update with nested iteration
                        ? updateDeepArrayObjects(child, {
                            ...(value ?? defaultValue),
                            ...el,
                        })
                        : (value ?? defaultValue)),
                }
                : el)
            : [
                // create from scratch if any of the iterations array keys don't exist
                ...(obj[fieldName] || []),
                {
                    [keyName]: id,
                    ...(child
                        ? updateDeepArrayObjects(child, value ?? defaultValue)
                        : (value ?? defaultValue)),
                },
            ],
    };
}
